
import React, { useState, useMemo } from 'react';
import { Story } from '../types';
import Button from './Button';

interface ExploreViewProps {
  t: any;
  stories: any[];
  onSelect: (id: string) => void;
  onBack: () => void;
}

const ITEMS_PER_PAGE = 8;

const ExploreView: React.FC<ExploreViewProps> = ({ t, stories, onSelect, onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const filteredStories = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return stories || [];
    return (stories || []).filter(story => 
      (story.title || "").toLowerCase().includes(term)
    );
  }, [stories, searchTerm]);

  const totalPages = Math.ceil(filteredStories.length / ITEMS_PER_PAGE);
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredStories.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredStories, currentPage]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-fadeIn space-y-10">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="space-y-2 text-center md:text-left">
           <button onClick={onBack} className="group text-indigo-400 font-bold text-[10px] uppercase tracking-widest hover:text-indigo-600 transition-colors flex items-center gap-2 outline-none">
             <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-all">
               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m15 18-6-6 6-6"/></svg>
             </div>
             {t.back}
           </button>
           <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-indigo-950 leading-tight">{t.showcaseTitle}</h1>
           <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">{t.showcaseDesc}</p>
        </div>

        <div className="w-full md:max-w-md relative group">
          <input 
            type="text" 
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder={t.searchPlaceholder}
            className="w-full pl-6 pr-6 py-4 bg-white rounded-2xl border-2 border-indigo-50 focus:border-indigo-600 outline-none shadow-lg shadow-indigo-100/10 transition-all font-bold text-indigo-950 text-sm"
          />
        </div>
      </div>

      {filteredStories.length === 0 ? (
        <div className="text-center py-20 bg-indigo-50/30 rounded-[2rem] border-2 border-dashed border-indigo-100">
           <div className="text-6xl mb-4 opacity-40">ⵣ</div>
           <p className="text-indigo-300 font-bold text-lg uppercase tracking-widest">{t.noResults}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {currentItems.map((story) => (
            <div 
              key={story.id} 
              onClick={() => onSelect(story.id)}
              className="group bg-white rounded-2xl p-4 shadow-sm border border-indigo-50/50 cursor-pointer hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full"
            >
              <div className="aspect-[4/5] rounded-xl overflow-hidden relative mb-4 bg-indigo-50 flex items-center justify-center border border-white group-hover:bg-indigo-100 transition-colors">
                {story.coverImage && !imgErrors[story.id] ? (
                  <img 
                    src={story.coverImage} 
                    alt={story.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700" 
                    loading="lazy"
                    onError={() => setImgErrors(p => ({...p, [story.id]: true}))}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 opacity-30 select-none bg-indigo-50 w-full h-full justify-center">
                    <span className="text-6xl font-tifinagh text-indigo-300">ⵣ</span>
                    <span className="text-[10px] font-black uppercase tracking-tighter text-indigo-400">Pas d'image</span>
                  </div>
                )}
                <div className="absolute top-4 left-4 z-10">
                  <span className="glass px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest text-indigo-600 shadow-sm border border-white/40">ⵣ ⵜⴰⵏⴼⵓⵙⵜ</span>
                </div>
              </div>
              <div className="text-center px-2 pb-2 mt-auto">
                <h3 className="text-lg font-bold text-indigo-950 font-tifinagh group-hover:text-indigo-600 transition-colors line-clamp-2">
                  {story.title}
                </h3>
                <div className="flex items-center justify-center gap-2 pt-2">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Conte Magique</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-12">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
            className="w-10 h-10 rounded-xl flex items-center justify-center bg-white border-2 border-indigo-50 text-indigo-600 disabled:opacity-30 hover:border-indigo-600 transition-all"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="4" ><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <span className="text-indigo-950 font-black text-lg">{currentPage} / {totalPages}</span>
          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
            className="w-10 h-10 rounded-xl flex items-center justify-center bg-white border-2 border-indigo-50 text-indigo-600 disabled:opacity-30 hover:border-indigo-600 transition-all"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="4"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ExploreView;
